// SystemStatusSection.jsx
// Versión "embebida" del panel de status para mostrar dentro del Dashboard.
// Usa el mismo hook useSystemHealth que el indicador del header → comparten polling.
// 2026-06-22: tabs General vs Limpack para separar terceros vs sistemas propios.

import { useMemo, useState } from 'react'
import { useSystemHealth } from '../lib/useSystemHealth'

// Orden: cortas primero (rellenan filas), Edge Functions al final (es la más larga, 13 items)
const CATEGORIAS = [
  { v: 'sitios',         l: '🌐 Sitios web', orden: 1 },
  { v: 'backend',        l: '⚙ Backend Supabase', orden: 2 },
  { v: 'apis',           l: '🔌 APIs externas', orden: 3 },
  { v: 'flags',          l: '🚦 Flags y configs', orden: 4 },
  { v: 'metricas',       l: '📊 Métricas operativas', orden: 5 },
  { v: 'edge_functions', l: '⚡ Edge Functions', orden: 6 },
]

const ESTADO_ICONO = { ok: '🟢', warn: '🟡', error: '🔴' }

const RESUMEN_TEXT = {
  ok:    { titulo: '🟢 Todo OK',        color: '#16A34A' },
  warn:  { titulo: '🟡 Hay que mirar',   color: '#CA8A04' },
  error: { titulo: '🔴 Algo crítico cayó', color: '#DC2626' },
}

// Tabs: separamos "general" (terceros que no controlamos) de "específica Limpack" (propios).
// Edge functions tienen id que arranca con 'ef_'; quedan en Limpack.
// Lo que viene del array CHEQUEOS con categoria='apis' es siempre terceros (Meta, Plexo, Biller, etc.).
function esGeneral(s) {
  return s.categoria === 'apis' && !String(s.id || '').startsWith('ef_')
}
function esLimpack(s) {
  return ['sitios', 'backend', 'flags', 'metricas', 'edge_functions'].includes(s.categoria)
}

const VISTAS = [
  { v: 'todo',    l: 'Todo' },
  { v: 'general', l: '🌍 General',  desc: 'Servicios de terceros (Meta, Plexo, Biller, Supabase, Cloudflare…)' },
  { v: 'limpack', l: '🧼 Limpack',  desc: 'Sistemas propios (sitios, edge functions, BD)' },
]

export default function SystemStatusSection() {
  const { servicios, resumen, timestamp, cargando, refrescar } = useSystemHealth()
  const [vista, setVista] = useState('todo')

  // Filtra servicios por vista
  const serviciosFiltrados = useMemo(() => {
    if (vista === 'general') return servicios.filter(esGeneral)
    if (vista === 'limpack') return servicios.filter(esLimpack)
    return servicios
  }, [servicios, vista])

  // Recalcula resumen sobre la vista activa
  const resumenVista = useMemo(() => {
    let ok = 0, warn = 0, err = 0, criticosError = 0
    for (const s of serviciosFiltrados) {
      if (s.estado === 'ok') ok++
      else if (s.estado === 'error') { err++; if (s.critico) criticosError++ }
      else if (s.estado === 'warn') warn++
    }
    const estadoGlobal = criticosError > 0 ? 'error' : (err > 0 || warn > 0) ? 'warn' : 'ok'
    return { total: serviciosFiltrados.length, ok, warn, error: err, criticosError, estadoGlobal }
  }, [serviciosFiltrados])

  const meta = RESUMEN_TEXT[resumenVista.estadoGlobal] || RESUMEN_TEXT.ok
  const vistaActual = VISTAS.find(v => v.v === vista) || VISTAS[0]

  return (
    <section className="ss-section">
      <header className="ss-head">
        <div>
          <h2 style={{ color: meta.color }}>{meta.titulo}</h2>
          <div className="ss-sub">
            <span>{resumenVista.ok} OK</span>
            {resumenVista.warn > 0 && <span className="ss-sub-warn"> · {resumenVista.warn} atención</span>}
            {resumenVista.error > 0 && <span className="ss-sub-err"> · {resumenVista.error} con problema</span>}
            {timestamp && (
              <> · última verificación {new Date(timestamp).toLocaleTimeString('es-UY')}</>
            )}
          </div>
        </div>
        <button className="ss-refresh" onClick={refrescar} disabled={cargando}>
          {cargando ? '⏳ Verificando…' : '↻ Refrescar'}
        </button>
      </header>

      {/* Tabs General vs Limpack */}
      <div className="ss-tabs" role="tablist" aria-label="Filtrar por origen del servicio">
        {VISTAS.map(v => {
          const count = v.v === 'todo' ? servicios.length
            : v.v === 'general' ? servicios.filter(esGeneral).length
            : servicios.filter(esLimpack).length
          const activo = v.v === vista
          return (
            <button key={v.v} role="tab" aria-selected={activo}
                    className={`ss-tab ${activo ? 'ss-tab-on' : ''}`}
                    onClick={() => setVista(v.v)}>
              {v.l} <span className="ss-tab-count">{count}</span>
            </button>
          )
        })}
      </div>
      {vistaActual.desc && (
        <div className="ss-vista-desc">{vistaActual.desc}</div>
      )}

      <div className="ss-grid">
        {CATEGORIAS.map(cat => {
          const items = serviciosFiltrados.filter(s => s.categoria === cat.v)
          if (items.length === 0) return null
          const conProblemas = items.filter(s => s.estado === 'error' || s.estado === 'warn').length
          return (
            <div key={cat.v} className="ss-card">
              <div className="ss-card-head">
                <strong>{cat.l}</strong>
                <span className="ss-card-meta">{items.length}{conProblemas > 0 && ` · ${conProblemas} con tema`}</span>
              </div>
              <ul className="ss-list">
                {items.map(s => (
                  <li key={s.id} className={`ss-item ss-${s.estado}`}>
                    <span className="ss-ico">{ESTADO_ICONO[s.estado] || '⚪'}</span>
                    <div className="ss-item-body">
                      <div className="ss-item-nombre">
                        {s.nombre}
                        {s.critico && <span className="ss-item-crit">crítico</span>}
                      </div>
                      <div className="ss-item-detalle">{s.detalle || '—'}</div>
                    </div>
                    {s.valor !== undefined && (
                      <div className="ss-item-valor">{s.valor.toLocaleString('es-UY')}</div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )
        })}
      </div>

      <Estilos />
    </section>
  )
}

function Estilos() {
  return (
    <style>{`
      .ss-section { margin-bottom: 24px; }
      .ss-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
      .ss-head h2 { margin: 0 0 4px; font-size: 17px; font-weight: 800; letter-spacing: -0.01em; }
      .ss-sub { font-size: 12.5px; color: #64748B; }
      .ss-sub-warn { color: #CA8A04; font-weight: 600; }
      .ss-sub-err { color: #DC2626; font-weight: 700; }
      .ss-refresh {
        background: #fff; border: 1px solid #E2E8F0; border-radius: 8px;
        padding: 7px 12px; font-size: 12.5px; cursor: pointer; color: #475569;
        font-family: inherit; font-weight: 600;
      }
      .ss-refresh:hover { border-color: #1A3461; color: #1A3461; }
      .ss-refresh:disabled { opacity: 0.5; cursor: wait; }

      /* Tabs General vs Limpack */
      .ss-tabs {
        display: flex; gap: 6px; margin: 0 0 6px;
        padding: 4px; background: #F1F5F9; border-radius: 10px;
        width: fit-content; max-width: 100%; overflow-x: auto;
      }
      .ss-tab {
        background: transparent; border: none; padding: 7px 14px;
        font-family: inherit; font-size: 12.5px; font-weight: 700;
        color: #64748B; cursor: pointer; border-radius: 7px;
        display: inline-flex; align-items: center; gap: 6px;
        white-space: nowrap; transition: background 80ms, color 80ms;
      }
      .ss-tab:hover { color: #1A3461; }
      .ss-tab-on { background: #fff; color: #1A3461; box-shadow: 0 1px 3px rgba(0,0,0,0.06); }
      .ss-tab-count {
        font-size: 10.5px; padding: 1px 6px; border-radius: 999px;
        background: rgba(26,52,97,0.08); color: inherit; font-weight: 700;
      }
      .ss-tab-on .ss-tab-count { background: rgba(0,168,181,0.15); color: #0E7A82; }
      .ss-vista-desc { font-size: 11.5px; color: #94A3B8; margin: 0 0 12px 4px; font-style: italic; }

      /* Multi-column masonry REAL — cada columna se llena de arriba a abajo, sin huecos en blanco */
      .ss-grid {
        column-count: 3;
        column-gap: 12px;
      }
      @media (max-width: 1100px) { .ss-grid { column-count: 2; } }
      @media (max-width: 700px)  { .ss-grid { column-count: 1; } }

      .ss-card {
        background: #fff; border: 1px solid #E2E8F0; border-radius: 12px;
        padding: 12px 14px;
        margin: 0 0 12px;
        break-inside: avoid;
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
      }
      .ss-card-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 8px; gap: 8px; }
      .ss-card-head strong { font-size: 11.5px; color: #1A3461; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 800; }
      .ss-card-meta { font-size: 11px; color: #64748B; }

      .ss-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 4px; }
      .ss-item {
        display: flex; gap: 8px; align-items: center;
        padding: 8px 10px; border-radius: 6px; background: #F8FAFC; font-size: 12.5px;
      }
      .ss-item.ss-warn { background: #FEF3C7; }
      .ss-item.ss-error { background: #FEE2E2; }
      .ss-ico { font-size: 12px; flex-shrink: 0; }
      .ss-item-body { flex: 1; min-width: 0; }
      .ss-item-nombre { font-weight: 700; color: #1A3461; display: flex; align-items: center; gap: 6px; line-height: 1.2; }
      .ss-item-detalle { font-size: 10.5px; color: #64748B; margin-top: 1px; word-break: break-word; }
      .ss-item-valor { font-weight: 800; color: #1A3461; font-size: 15px; font-variant-numeric: tabular-nums; }
      .ss-item-crit {
        background: #1A3461; color: #fff; font-size: 9px; padding: 1px 5px;
        border-radius: 999px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.04em;
      }
    `}</style>
  )
}
